import { useRef, type ReactNode } from "react";
import { Spinner } from "@/shared/components/spinner";
import { cn } from "@/shared/lib/utils";

// How close to the bottom (px) of the scroll container before the next page loads.
const LOAD_MORE_THRESHOLD_PX = 96;

/**
 * Reusable "fetch more when scrolled near the bottom" scroll container — the
 * scroll-threshold + trailing-spinner mechanic UploadHistoryList already
 * used for upload history, extracted here so any other paginated list
 * (e.g. the imaging-studies list) reuses the exact same mechanic instead of
 * re-deriving its own onScroll handler. Owns only the scrollable wrapper and
 * the "loading more" spinner at the bottom — the rows themselves are
 * `children`, however the caller wants them grouped/rendered.
 */
export function InfiniteScrollContainer({
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  className,
  children,
}: {
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  className?: string;
  children: ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || !hasMore || isLoadingMore || !onLoadMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX) onLoadMore();
  };

  return (
    <div ref={scrollRef} onScroll={handleScroll} className={cn("overflow-y-auto", className)}>
      {children}
      {isLoadingMore && (
        <div className="flex items-center justify-center py-4">
          <Spinner size="small" />
        </div>
      )}
    </div>
  );
}
